import { createContext, useContext, useMemo, type ReactNode } from "react";
import { Effect, Match } from "effect";
import { Atom } from "effect/unstable/reactivity";
import { useAtomSet, useAtomValue } from "@effect/atom-react";
import { makeEditorDocumentCommand, type EditorDocumentAction } from "./documents-commands.ts";
import type { FrontendRoot } from "../state/root-owner.ts";
import type { EditorDocuments } from "./documents-service.ts";
import type { EditorDocumentHydration, EditorDocumentSnapshot, EditorFilesUpdate, EditorFoldersUpdate } from "./documents-model.ts";

interface Root {
  readonly atoms: FrontendRoot<EditorDocuments>["atoms"];
  readonly documents: typeof EditorDocuments.Service;
  readonly owner: FrontendRoot<EditorDocuments>["owner"];
}
const EditorContext = createContext<Root | undefined>(undefined);
export const EditorDocumentsProvider = ({ value, children }: { readonly value: Root; readonly children: ReactNode }) =>
  <EditorContext.Provider value={value}>{children}</EditorContext.Provider>;

const blank: EditorDocumentSnapshot = {
  workspaceId: null, generation: 0, hydrationSequence: 0, revision: 0,
  files: {}, folders: [], closed: false
};

export function useEditorDocuments() {
  const root = useContext(EditorContext);
  const empty = useMemo(() => Atom.make(Effect.succeed(blank)), []);
  const observed = useAtomValue(root?.documents.snapshot ?? empty);
  const snapshot = Match.value(observed).pipe(
    Match.tag("Failure", () => root?.documents.initial ?? blank),
    Match.tag("Initial", () => root?.documents.initial ?? blank),
    Match.tag("Success", value => value.value),
    Match.exhaustive
  );
  const commands = useMemo(() => root
    ? root.atoms.fn(makeEditorDocumentCommand(root.documents, root.owner), { concurrent: true })
    : Atom.fn((_: EditorDocumentAction) => Effect.void), [root]);
  const dispatch = useAtomSet(commands);
  const identity = { workspaceId: snapshot.workspaceId, generation: snapshot.generation };
  return {
    snapshot, files: snapshot.files, folders: snapshot.folders,
    setFiles: (update: EditorFilesUpdate) => dispatch({ tag: "Files", identity, update }),
    setFolders: (update: EditorFoldersUpdate) => dispatch({ tag: "Folders", identity, update }),
    hydrate: (input: EditorDocumentHydration, after: () => void) => dispatch({ tag: "Hydrate", input, after })
  };
}
